/*
 * tt-search-drawer.js — the search icon opens a field over the nav, and the
 * results arrive in a drawer.
 *
 * Josh, 2026-08-20: "I don't think the search icon should go to a different
 * page. I think it should animate reveal a search bar overtop the nav bar and
 * searching should provide results in a sidebar drawer."
 *
 * PROGRESSIVE ENHANCEMENT, and it is not decoration here. The trigger is a real
 * <a href="/search"> and the field is a real GET form, so with this file absent
 * or broken the icon still goes to the search page and the field still submits
 * to it. Everything below is an upgrade over a working baseline; nothing is the
 * only route to anything.
 *
 * RESULTS COME FROM A SECTION, not from search/suggest.json. See
 * sections/tt-predictive-search.liquid — the short version is that the JSON
 * endpoint answers with a bare "1899.99" and formatting money in JavaScript
 * means reimplementing money_format per market. Liquid already knows.
 *
 * THE THINGS THAT GO WRONG IN A TYPEAHEAD, and what each one is answered by:
 *
 *   Out-of-order responses. Keystroke 3 can answer after keystroke 5 and paint
 *   stale results over fresh ones. Every request carries a sequence number and
 *   anything older than the last painted one is dropped.
 *
 *   A request per keystroke. Debounced, and an in-flight request is aborted
 *   when a newer one starts.
 *
 *   The drawer flashing empty between keystrokes. It does not clear; it dims,
 *   via aria-busy, and the previous results stay legible until the new ones
 *   land.
 *
 *   Focus escaping the drawer. Tab is trapped between the field and the last
 *   focusable row while it is open.
 */

(() => {
  const PANEL = document.getElementById('tt-search-panel');
  const DRAWER = document.getElementById('tt-search-drawer');
  if (!PANEL || !DRAWER) return;

  const input = PANEL.querySelector('[data-search-input]');
  const form = PANEL.querySelector('.tt-sp__form');
  const clear = PANEL.querySelector('[data-search-clear]');
  const results = DRAWER.querySelector('[data-search-results]');
  const termsOut = DRAWER.querySelector('[data-search-terms]');
  const allLink = DRAWER.querySelector('[data-search-all]');
  const allLabel = DRAWER.querySelector('[data-search-all-label]');
  if (!input || !results) return;

  /* The resting state is markup, not a string built here, so it survives a
     failed fetch and needs no second copy of the links. */
  const idle = results.innerHTML;

  const SEARCH_URL = (form && form.getAttribute('action')) || '/search';
  const DEBOUNCE = 220;
  const MIN = 2;

  let open = false;
  let timer = null;
  let controller = null;
  let seq = 0;
  let painted = 0;
  let lastQuery = '';
  let opener = null;

  /* ---------------------------------------------------------------- opening */

  function openSearch(from) {
    if (open) return;
    open = true;
    opener = from || null;

    PANEL.hidden = false;
    DRAWER.hidden = false;
    /* Two frames, not one. The element has just left `hidden`, and adding the
       class in the same frame means the browser never computes the closed
       state, so there is nothing to transition FROM and the panel simply
       appears. This is the same double-rAF the nav drawer uses. */
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        PANEL.classList.add('is-open');
        DRAWER.classList.add('is-open');
      });
    });

    document.body.style.overflow = 'hidden';
    input.setAttribute('aria-expanded', 'true');

    /* Focus AFTER the reveal starts. Focusing a field mid-animation makes some
       browsers scroll the page to it, which on a fixed bar means scrolling to
       the top of a page the shopper was reading. */
    setTimeout(() => {
      input.focus();
      input.select();
    }, 60);
  }

  function closeSearch() {
    if (!open) return;
    open = false;

    PANEL.classList.remove('is-open');
    DRAWER.classList.remove('is-open');
    document.body.style.overflow = '';
    input.setAttribute('aria-expanded', 'false');

    if (controller) {
      controller.abort();
      controller = null;
    }
    clearTimeout(timer);

    /* Hide only once the transition has run, and only if nothing reopened it in
       the meantime — a fast close/open would otherwise hide a panel that is on
       its way in. */
    setTimeout(() => {
      if (!open) {
        PANEL.hidden = true;
        DRAWER.hidden = true;
      }
    }, 320);

    if (opener && document.contains(opener)) opener.focus();
    opener = null;
  }

  /* ---------------------------------------------------------------- fetching */

  function paintIdle() {
    results.innerHTML = idle;
    results.setAttribute('aria-busy', 'false');
    if (termsOut) termsOut.textContent = '';
    if (allLink) allLink.hidden = true;
  }

  async function run(query) {
    const mine = ++seq;
    if (controller) controller.abort();
    controller = new AbortController();
    results.setAttribute('aria-busy', 'true');

    const url =
      SEARCH_URL +
      '/suggest?q=' +
      encodeURIComponent(query) +
      '&resources[type]=product,collection,page,article' +
      '&resources[limit]=6' +
      '&section_id=tt-predictive-search';

    try {
      const res = await fetch(url, { signal: controller.signal });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const text = await res.text();

      /* STALE RESPONSE. Keystroke 3 answering after keystroke 5 would otherwise
         paint the older results and leave them there until the next keystroke. */
      if (mine < painted) return;
      painted = mine;

      /* A section_id response arrives inside Shopify's own
         <div id="shopify-section-…"> wrapper. Lift the contents out so the
         drawer's own layout applies to the groups directly. */
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const section = doc.querySelector('[id^="shopify-section-"]');
      results.innerHTML = section ? section.innerHTML : text;
      results.setAttribute('aria-busy', 'false');

      if (termsOut) termsOut.textContent = '“' + query + '”';
      if (allLink && allLabel) {
        allLink.hidden = false;
        allLink.href = SEARCH_URL + '?q=' + encodeURIComponent(query);
        allLabel.textContent = 'See all results for “' + query + '”';
      }
    } catch (e) {
      if (e.name === 'AbortError') return;
      /* A FAILED FETCH IS NOT AN EMPTY RESULT, and showing "nothing matched"
         for a network error sends the shopper looking for a different word for
         something the store does sell. Fall back to the route that always
         works. */
      if (mine < painted) return;
      painted = mine;
      results.setAttribute('aria-busy', 'false');
      results.innerHTML =
        '<div class="tt-sd__empty"><p class="tt-sd__empty-head">Search is not answering.</p>' +
        '<p class="tt-sd__empty-body">Open the full results page instead.</p></div>';
      if (allLink && allLabel) {
        allLink.hidden = false;
        allLink.href = SEARCH_URL + '?q=' + encodeURIComponent(query);
        allLabel.textContent = 'Search for “' + query + '”';
      }
    } finally {
      controller = null;
    }
  }

  function onInput() {
    const query = input.value.trim();
    if (clear) clear.hidden = query.length === 0;
    if (query === lastQuery) return;
    lastQuery = query;

    clearTimeout(timer);
    if (query.length < MIN) {
      if (controller) controller.abort();
      paintIdle();
      return;
    }
    timer = setTimeout(() => run(query), DEBOUNCE);
  }

  /* ---------------------------------------------------------------- wiring */

  input.addEventListener('input', onInput);

  if (clear) {
    clear.addEventListener('click', () => {
      input.value = '';
      lastQuery = '';
      clear.hidden = true;
      paintIdle();
      input.focus();
    });
  }

  DRAWER.querySelectorAll('[data-search-close]').forEach((el) => {
    el.addEventListener('click', closeSearch);
  });
  PANEL.querySelectorAll('[data-search-close]').forEach((el) => {
    el.addEventListener('click', closeSearch);
  });

  /* The nav's search icon. It stays an anchor to /search so the no-script route
     is real; the click is upgraded here. Modifier-clicks are left alone so
     "open in a new tab" still means that. */
  document.querySelectorAll('[data-search-open]').forEach((el) => {
    el.addEventListener('click', (e) => {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
      e.preventDefault();
      openSearch(el);
    });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && open) {
      e.preventDefault();
      closeSearch();
      return;
    }

    /* "/" opens search, the convention every catalogue site with a lot of SKUs
       has settled on — but never while the shopper is typing into something
       else, which is what makes it safe. */
    if (e.key === '/' && !open) {
      const t = e.target;
      const typing =
        t &&
        (t.tagName === 'INPUT' ||
          t.tagName === 'TEXTAREA' ||
          t.tagName === 'SELECT' ||
          t.isContentEditable);
      if (typing) return;
      e.preventDefault();
      openSearch(null);
    }
  });

  /* FOCUS TRAP. A dialog whose Tab escapes into the page behind it is a dialog
     for sighted mouse users only. The field and the close button live in the
     bar rather than in the drawer, so the trap spans both. */
  document.addEventListener('keydown', (e) => {
    if (!open || e.key !== 'Tab') return;
    const focusable = [
      ...PANEL.querySelectorAll('a[href], button:not([hidden]), input:not([type="hidden"])'),
      ...DRAWER.querySelectorAll('a[href]:not([hidden]), button:not([hidden])'),
    ].filter((el) => el.offsetParent !== null || el === input);
    if (!focusable.length) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  });

  /* Submitting with fewer than MIN characters would land on an empty results
     page; below the threshold the form is simply not sent. */
  if (form) {
    form.addEventListener('submit', (e) => {
      if (input.value.trim().length === 0) e.preventDefault();
    });
  }
})();
